/**
 * V2 NFT API Service
 * V1 NFTAPI를 대체
 */

import { apiInterceptor } from '@/networks/api/interceptor'

/**
 * NFT 아이템 인터페이스 (백엔드 NftItemDto와 일치)
 */
export interface INFT {
  id: string
  userAddress?: string // refresh 응답에는 없을 수 있음
  currencyId: string
  contractAddress: string
  tokenId: string
  name?: string
  description?: string
  imageUrl?: string
  standard?: string // ERC721, ERC1155
  balance: string // NFT 수량 (ERC1155의 경우)
  isVisible: boolean
  isSpam: boolean
  isNsfw: boolean
  isFavorite: boolean
  metadata?: any
  createdAt?: Date // refresh 응답에는 없을 수 있음
  updatedAt?: Date // refresh 응답에는 없을 수 있음
  lastRefreshed?: Date

  // 호환성을 위한 필드
  accountId?: string
  network?: string
  owner?: string
  tokenUri?: string

  // V1 호환: NFTTokenState (active=1, inactive=2, pending=3)
  state?: number

  // V2 추가 필드
  externalLink?: string
  addtionalUrl?: {
    image?: string
    content?: { name: string; description: string }
  }
  nftContractId?: string
  nftContract?: {
    id: string
    currencyId: string
    address: string
    name: string
    symbol?: string
    schemaName?: string
    metadata?: {
      imageUrl?: string
      description?: string
      externalLink?: string
    }
  }
}

/**
 * NFT 목록 응답 인터페이스
 */
export interface INFTListResponse {
  items: INFT[]
  total: number
  page?: number
  limit?: number
  cursor?: string
}

export interface INFTAPIService {
  getNFTList(params?: {
    userId?: string
    walletId?: string
    userAddress?: string
    currencyId?: string
    includeSpam?: boolean
    page?: number
    limit?: number
  }): Promise<INFTListResponse>
  getNFTById(nftId: string): Promise<INFT>
  refreshNFTs(params?: {
    userId?: string
    walletId?: string
    userAddress?: string
    currencyId?: string
    includeSpam?: boolean
    page?: number
    limit?: number
  }): Promise<INFTListResponse>
  updateNFTVisibility(nftId: string, isVisible: boolean): Promise<INFT>
  getSupportedNetworks(): Promise<string[]>
}

export class NFTAPIService implements INFTAPIService {
  /**
   * NFT 목록 조회 (V2)
   * V1: GET /nfttoken/me
   * V2: GET /nft
   *
   * 주의: V2 API는 userId와 walletId 또는 userAddress가 필요함
   */
  async getNFTList(params?: {
    userId?: string
    walletId?: string
    userAddress?: string
    currencyId?: string
    includeSpam?: boolean
    page?: number
    limit?: number
  }): Promise<INFTListResponse> {
    const queryParams = new URLSearchParams()

    // V2 API 필수 파라미터 처리
    // userId와 walletId를 localStorage에서 가져오기
    const userId = params?.userId || localStorage.getItem('userId') || ''
    const walletId =
      params?.walletId || localStorage.getItem('defaultWalletId') || ''

    if (userId && walletId) {
      queryParams.append('userId', userId)
      queryParams.append('walletId', walletId)
    } else if (params?.userAddress) {
      queryParams.append('userAddress', params.userAddress)
    }

    if (params?.currencyId) {
      queryParams.append('currencyId', params.currencyId)
    }
    if (params?.includeSpam !== undefined) {
      queryParams.append('includeSpam', params.includeSpam.toString())
    }
    if (params?.page) {
      queryParams.append('page', params.page.toString())
    }
    if (params?.limit) {
      queryParams.append('limit', params.limit.toString())
    }

    const queryString = queryParams.toString()
    const endpoint = queryString ? `/nft?${queryString}` : '/nft'

    const response = await apiInterceptor.request(endpoint)
    if (!response.ok) {
      throw new Error(`Failed to fetch NFTs: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리: { success: true, data: { nfts, total, ... } }
    if (data.success && data.data) {
      const responseData = data.data
      return {
        items: (responseData.nfts || []).map(this.transformNFT),
        total: responseData.total || 0,
        page: responseData.page,
        limit: responseData.limit,
        cursor: responseData.cursor,
      }
    }

    // 직접 응답 처리: { nfts, total, page, limit }
    if ('nfts' in data && Array.isArray(data.nfts)) {
      return {
        items: data.nfts.map(this.transformNFT),
        total: data.total || data.nfts.length,
        page: data.page,
        limit: data.limit,
      }
    }

    // 배열 직접 응답
    if (Array.isArray(data)) {
      return {
        items: data.map(this.transformNFT),
        total: data.length,
      }
    }

    return {
      items: [],
      total: data.total || 0,
      page: data.page,
      limit: data.limit,
    }
  }

  /**
   * 단일 NFT 조회 (V2)
   * V1: GET /nfttoken/{nftTokenId}
   * V2: GET /nft/{nftId}
   */
  async getNFTById(nftId: string): Promise<INFT> {
    const response = await apiInterceptor.request(`/nft/${nftId}`)
    if (!response.ok) {
      throw new Error(`Failed to fetch NFT: ${response.statusText}`)
    }

    const data = await response.json()
    return this.transformNFT(data.nft || data)
  }

  /**
   * NFT 새로고침 (V2)
   * V1: POST /nfttoken/refresh (accountIds 전달)
   * V2: POST /nft/refresh (userId, walletId 전달)
   *
   * V2 백엔드:
   * - userId만 전달: 해당 유저의 모든 지갑 새로고침
   * - userId + walletId: 특정 지갑만 새로고침
   * 새로고침 후 업데이트된 NFT 목록을 반환
   */
  async refreshNFTs(params?: {
    userId?: string
    walletId?: string
    userAddress?: string
    currencyId?: string
    includeSpam?: boolean
    page?: number
    limit?: number
  }): Promise<INFTListResponse> {
    // localStorage에서 userId 가져오기
    const userId = params?.userId || localStorage.getItem('userId') || ''

    const requestBody: any = {}

    // userId는 필수
    if (userId) {
      requestBody.userId = userId
    } else if (params?.userAddress) {
      requestBody.userAddress = params.userAddress
    }

    // walletId는 선택 (없으면 모든 지갑 새로고침)
    if (params?.walletId) {
      requestBody.walletId = params.walletId
    }

    if (params?.currencyId) {
      requestBody.currencyId = params.currencyId
    }
    if (params?.includeSpam !== undefined) {
      requestBody.includeSpam = params.includeSpam
    }
    if (params?.page) {
      requestBody.page = params.page
    }
    if (params?.limit) {
      requestBody.limit = params.limit
    }

    const response = await apiInterceptor.request('/nft/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed to refresh NFTs: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리: { success: true, data: { nfts, total, ... } }
    if (data.success && data.data) {
      const responseData = data.data
      return {
        items: (responseData.nfts || []).map(this.transformNFT),
        total: responseData.total || 0,
        page: responseData.page,
        limit: responseData.limit,
        cursor: responseData.cursor,
      }
    }

    // 직접 응답 처리: { nfts, total, page, limit }
    if ('nfts' in data && Array.isArray(data.nfts)) {
      return {
        items: data.nfts.map(this.transformNFT),
        total: data.total || data.nfts.length,
        page: data.page,
        limit: data.limit,
      }
    }

    return { items: [], total: 0 }
  }

  /**
   * NFT 표시/숨기기 (V2)
   * V2: PATCH /nft/:id/visibility
   */
  async updateNFTVisibility(nftId: string, isVisible: boolean): Promise<INFT> {
    const response = await apiInterceptor.request(`/nft/${nftId}/visibility`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ isVisible }),
    })

    if (!response.ok) {
      throw new Error(`Failed to update NFT visibility: ${response.statusText}`)
    }

    const data = await response.json()

    // V2 응답 구조 처리
    if (data.success && data.data) {
      return this.transformNFT(data.data)
    }

    return this.transformNFT(data)
  }

  /**
   * 지원되는 네트워크 조회 (V2)
   * V1: GET /nfttoken/support-network
   * V2: GET /nft/supported-networks
   *
   * 백엔드는 객체 배열을 반환하지만, 프론트엔드에서는 이름만 추출
   */
  async getSupportedNetworks(): Promise<string[]> {
    try {
      const response = await apiInterceptor.request('/nft/supported-networks')

      if (!response.ok) {
        return this.getDefaultSupportedNetworks()
      }

      const data = await response.json()

      // 백엔드에서 에러 응답인 경우
      if (data.success === false) {
        return this.getDefaultSupportedNetworks()
      }

      // V2 응답 구조 처리
      const networks = data.networks || data.data || data

      // 객체 배열인 경우 name만 추출
      if (
        Array.isArray(networks) &&
        networks.length > 0 &&
        typeof networks[0] === 'object'
      ) {
        return networks.map(
          (network: any) => network.name || network.id || network,
        )
      }

      // 이미 문자열 배열인 경우
      if (Array.isArray(networks)) {
        return networks
      }

      return this.getDefaultSupportedNetworks()
    } catch {
      return this.getDefaultSupportedNetworks()
    }
  }

  /**
   * 기본 지원 네트워크 목록 (백엔드 에러 시 사용)
   */
  private getDefaultSupportedNetworks(): string[] {
    return [
      'ETHEREUM',
      'POLYGON',
      'BINANCE_SMART_CHAIN',
      'KLAYTN', // Kaia
      'ARBITRUM',
      'OPTIMISM',
      'AVALANCHE',
      'FANTOM',
      'CRONOS',
      'HARMONY',
    ]
  }

  /**
   * IPFS URL을 Gateway URL로 변환
   */
  private static replaceIPFSUrl(
    url: string | null | undefined,
  ): string | undefined {
    if (!url) return undefined

    const IPFS_PROTOCOL = 'ipfs://'
    const IPFS_GATEWAY = 'https://ipfs.io/ipfs/'
    const CF_IPFS_GATEWAY = 'https://cf-ipfs.com/ipfs/'

    // ipfs:// 프로토콜 변환
    if (url.startsWith(IPFS_PROTOCOL)) {
      return url.replace(IPFS_PROTOCOL, IPFS_GATEWAY)
    }

    // cf-ipfs.com을 ipfs.io로 변환 (안정성)
    if (url.startsWith(CF_IPFS_GATEWAY)) {
      return url.replace(CF_IPFS_GATEWAY, IPFS_GATEWAY)
    }

    return url
  }

  /**
   * NFT 데이터 변환 (백엔드 NftItemDto → 프론트엔드 INFT)
   * V2 API 응답 구조에 맞게 변환
   */
  private transformNFT(nft: any): INFT {
    // 1. imageUrl 우선순위 처리 (V2 구조)
    // nft.imageUrl > additionalUrl.image > nftContract.metadata.imageUrl > metadata.image
    let imageUrl =
      nft.imageUrl ||
      nft.additionalUrl?.image ||
      nft.metadata?.additionalUrl?.image ||
      nft.nftContract?.metadata?.imageUrl ||
      nft.metadata?.image ||
      nft.image

    // JSON 문자열인 경우 처리
    if (imageUrl && typeof imageUrl === 'string' && imageUrl.startsWith('{')) {
      try {
        const parsed = JSON.parse(imageUrl)
        imageUrl = parsed.ipfs || parsed.url || imageUrl
      } catch (e) {
        // JSON 파싱 실패 시 원본 사용
      }
    }

    // animation_url 우선 처리
    const animationUrl = nft.metadata?.animation_url
    if (animationUrl) {
      imageUrl = animationUrl
    }

    // IPFS URL 변환
    imageUrl = NFTAPIService.replaceIPFSUrl(imageUrl)

    // 2. name 우선순위 처리
    // nft.name (비어있지 않은 경우) > additionalUrl.content.name > nftContract.name > metadata.name
    let name = nft.name
    if (!name || name === '') {
      name =
        nft.additionalUrl?.content?.name ||
        nft.metadata?.additionalUrl?.content?.name ||
        nft.nftContract?.name ||
        nft.metadata?.name ||
        ''
    }

    // 3. description 우선순위 처리
    let description = nft.description
    if (!description || description === '') {
      description =
        nft.additionalUrl?.content?.description ||
        nft.metadata?.additionalUrl?.content?.description ||
        nft.nftContract?.metadata?.description ||
        nft.metadata?.description ||
        ''
    }

    // 4. externalLink는 metadata에 있음
    const externalLink =
      nft.externalLink ||
      nft.metadata?.externalLink ||
      nft.nftContract?.metadata?.externalLink ||
      ''

    // 5. additionalUrl 처리
    const additionalUrl =
      nft.additionalUrl || nft.metadata?.additionalUrl || undefined

    return {
      id: nft.id || nft.nftTokenId,
      userAddress: nft.userAddress || nft.owner || nft.ownerAddress,
      currencyId: nft.currencyId || nft.chainId || '',
      contractAddress: nft.contractAddress || nft.contract || '',
      tokenId: nft.tokenId || '',
      name: name,
      description: description,
      imageUrl: imageUrl,
      standard: nft.standard,
      balance: nft.balance || '1',
      isVisible: nft.isVisible !== undefined ? nft.isVisible : true,
      isSpam: nft.isSpam || false,
      isNsfw: nft.isNsfw || false,
      isFavorite: nft.isFavorite || false,
      metadata: nft.metadata,
      createdAt: nft.createdAt ? new Date(nft.createdAt) : undefined,
      updatedAt: nft.updatedAt ? new Date(nft.updatedAt) : undefined,
      lastRefreshed: nft.lastRefreshed
        ? new Date(nft.lastRefreshed)
        : undefined,

      // 호환성 필드 (V1 스타일)
      accountId: nft.accountId,
      network: nft.network || nft.chain || nft.currencyId,
      owner: nft.owner || nft.ownerAddress || nft.userAddress,
      tokenUri: nft.tokenUri,

      // V1 호환: NFTTokenState (active=1, inactive=2, pending=3)
      state: nft.state,

      // V2 추가 필드
      externalLink: externalLink,
      addtionalUrl: additionalUrl,
      nftContractId: nft.nftContractId,
      nftContract: nft.nftContract,
    }
  }
}

// 싱글톤 인스턴스
export const nftAPIService = new NFTAPIService()
